import { useState } from "react";
import {
  ChevronDown,
  FileUp,
  Folder,
  Link2,
  Loader2,
  Play,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import {
  ASSET_TYPES,
  ASSET_TYPE_LABEL,
  ASSET_VISIBILITIES,
  ASSET_VISIBILITY_LABEL,
  EMBED_PROVIDERS,
  EMBED_PROVIDER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  type AssetType,
  type AssetVisibility,
  type EmbedProvider,
  type PartnerType,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/format-api-error";
import { useCreateAsset } from "./use-create-asset";
import { useUploadContentAsset } from "./use-upload-content-asset";

// Phase 7 sub-bucket 13. SPA port of legacy app/admin/content/_components/
// AddAssetMenu.tsx. Four shapes (file / link / embed / folder); file mode
// drives the three-step upload via useUploadContentAsset and renders a
// progress bar fed by the new `onProgress` callback.
//
// Folders / external / embed shapes hit useCreateAsset directly (single
// POST). The shared form layout follows the legacy field order so admins
// don't have to relearn the dialog after the cutover.

type Mode = "file" | "link" | "embed" | "folder";

export function AddAssetMenu({
  parentAssetId,
  variant = "default",
}: {
  parentAssetId: string | null;
  variant?: "default" | "empty";
}) {
  const [mode, setMode] = useState<Mode | null>(null);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {variant === "empty" ? (
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add first asset
            </Button>
          ) : (
            <Button size="sm">
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add asset
              <ChevronDown className="ml-1 h-3.5 w-3.5" />
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setMode("file")}>
            <FileUp className="mr-2 h-3.5 w-3.5" />
            Upload file
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMode("link")}>
            <Link2 className="mr-2 h-3.5 w-3.5" />
            Web page URL
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMode("embed")}>
            <Play className="mr-2 h-3.5 w-3.5" />
            Embed (Loom, Slides…)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setMode("folder")}>
            <Folder className="mr-2 h-3.5 w-3.5" />
            Folder
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {mode != null && (
        <AddAssetDialog
          mode={mode}
          onClose={() => setMode(null)}
          parentAssetId={parentAssetId}
        />
      )}
    </>
  );
}

const TITLES: Record<Mode, string> = {
  file: "Upload file",
  link: "Add web page URL",
  embed: "Add embed",
  folder: "Add folder",
};
const DESCS: Record<Mode, string> = {
  file: "Drop a PDF, image, or video. Visibility controls who can see it.",
  link: "Paste an external URL. Partners get an Open button that opens in a new tab.",
  embed: "Pick a provider and paste the share URL. Embedded inline in the portal.",
  folder: "Folders group assets. Folders themselves have a visibility setting.",
};

function AddAssetDialog({
  mode,
  onClose,
  parentAssetId,
}: {
  mode: Mode;
  onClose: () => void;
  parentAssetId: string | null;
}) {
  const upload = useUploadContentAsset();
  const create = useCreateAsset();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assetType, setAssetType] = useState<AssetType>(
    mode === "embed" ? "video_link" : "deck",
  );
  const [visibility, setVisibility] = useState<AssetVisibility>("all_partners");
  const [partnerTypes, setPartnerTypes] = useState<Set<PartnerType>>(new Set());
  const [categoriesRaw, setCategoriesRaw] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [embedProvider, setEmbedProvider] = useState<EmbedProvider>("loom");
  const [embedUrl, setEmbedUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const pending = upload.isPending || create.isPending;

  function togglePartnerType(t: PartnerType) {
    setPartnerTypes((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }

  function parseCategories(): string[] {
    return categoriesRaw
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    const baseMetadata = {
      title: title.trim(),
      description: description.trim() || null,
      asset_type: mode === "folder" ? ("other" as AssetType) : assetType,
      is_folder: mode === "folder",
      parent_asset_id: parentAssetId,
      file_url: null,
      external_url: mode === "link" ? externalUrl.trim() : null,
      embed_provider: mode === "embed" ? embedProvider : null,
      embed_url: mode === "embed" ? embedUrl.trim() : null,
      visibility,
      partner_type_restriction: Array.from(partnerTypes),
      categories: parseCategories(),
      uploaded_by: null,
    };

    try {
      if (mode === "file") {
        if (!file) {
          toast.error("Pick a file to upload");
          return;
        }
        setProgress(0);
        await upload.mutateAsync({
          ...baseMetadata,
          file,
          onProgress: setProgress,
        });
        toast.success("Asset uploaded");
      } else {
        if (mode === "link" && !externalUrl.trim()) {
          toast.error("External URL is required");
          return;
        }
        if (mode === "embed" && !embedUrl.trim()) {
          toast.error("Embed URL is required");
          return;
        }
        await create.mutateAsync(baseMetadata);
        toast.success(mode === "folder" ? "Folder created" : "Asset added");
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to create asset"));
    } finally {
      setProgress(null);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && !pending && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{TITLES[mode]}</DialogTitle>
          <DialogDescription>{DESCS[mode]}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="title">{mode === "folder" ? "Folder name" : "Title"} *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {mode !== "folder" && (
            <div className="space-y-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {mode !== "folder" && (
              <div className="space-y-1.5">
                <Label htmlFor="asset_type">Type</Label>
                <select
                  id="asset_type"
                  value={assetType}
                  onChange={(e) => setAssetType(e.target.value as AssetType)}
                  className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
                >
                  {ASSET_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ASSET_TYPE_LABEL[t]}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="visibility">Visibility</Label>
              <select
                id="visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as AssetVisibility)}
                className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
              >
                {ASSET_VISIBILITIES.map((v) => (
                  <option key={v} value={v}>
                    {ASSET_VISIBILITY_LABEL[v]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Restrict to partner types (optional)</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {PARTNER_TYPES.map((t) => (
                <label
                  key={t}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted has-[:checked]:border-accent has-[:checked]:bg-accent/10 has-[:checked]:text-accent"
                >
                  <input
                    type="checkbox"
                    checked={partnerTypes.has(t)}
                    onChange={() => togglePartnerType(t)}
                    className="h-3 w-3 accent-accent"
                  />
                  {PARTNER_TYPE_LABEL[t]}
                </label>
              ))}
            </div>
          </div>

          {mode !== "folder" && (
            <div className="space-y-1.5">
              <Label htmlFor="categories">Categories (comma-separated)</Label>
              <Input
                id="categories"
                value={categoriesRaw}
                onChange={(e) => setCategoriesRaw(e.target.value)}
                placeholder="meeting link, slides, banner"
              />
            </div>
          )}

          {mode === "file" && (
            <div className="space-y-1.5 border-t pt-4">
              <Label htmlFor="file">File (PDF, DOCX, image, video — max 100 MB)</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                required
              />
              {progress !== null && (
                <UploadProgressBar value={progress} />
              )}
            </div>
          )}

          {mode === "link" && (
            <div className="space-y-1.5 border-t pt-4">
              <Label htmlFor="external_url">External URL *</Label>
              <Input
                id="external_url"
                type="url"
                value={externalUrl}
                onChange={(e) => setExternalUrl(e.target.value)}
                required
                placeholder="https://example.com/page"
              />
            </div>
          )}

          {mode === "embed" && (
            <div className="space-y-3 border-t pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="embed_provider">Provider *</Label>
                <select
                  id="embed_provider"
                  value={embedProvider}
                  onChange={(e) => setEmbedProvider(e.target.value as EmbedProvider)}
                  className="h-9 w-full rounded-md border bg-background px-2.5 text-sm"
                >
                  {EMBED_PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {EMBED_PROVIDER_LABEL[p]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="embed_url">Embed URL *</Label>
                <Input
                  id="embed_url"
                  type="url"
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.target.value)}
                  required
                  placeholder={placeholderForProvider(embedProvider)}
                />
                <p className="text-[11px] text-muted-foreground">
                  Paste the share URL. We normalise to the provider&apos;s embed
                  endpoint on render.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "folder" ? "Create folder" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UploadProgressBar({ value }: { value: number }) {
  return (
    <div className="mt-2">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-accent transition-[width] duration-150 ease-out"
          style={{ width: `${value}%` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
        Uploading… {value}%
      </p>
    </div>
  );
}

function placeholderForProvider(p: EmbedProvider): string {
  switch (p) {
    case "loom":
      return "https://www.loom.com/share/abc123…";
    case "youtube":
      return "https://www.youtube.com/watch?v=…";
    case "vimeo":
      return "https://vimeo.com/123456789";
    case "google_slides":
      return "https://docs.google.com/presentation/d/…/edit";
    case "google_docs":
      return "https://docs.google.com/document/d/…/edit";
    case "google_sheets":
      return "https://docs.google.com/spreadsheets/d/…/edit";
    case "hubspot_meeting":
      return "https://meetings.hubspot.com/owner/slug";
    case "linkedin":
      return "https://www.linkedin.com/embed/feed/update/urn:li:share:…";
    case "webpage":
      return "https://example.com/page";
  }
}
